// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from "react";
import ReactDOM from "react-dom";
import "./ClaimModals.scss";

import { functions, storage } from "../../context/FirebaseConfig";
import { useTranslation } from "react-i18next";
import { deleteField } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { Modal, Button } from 'react-bootstrap';
import { format } from "date-fns";

import PropertyField from "../PropertyField/PropertyField";
import LoadingIcon from "../LoadingIcon/LoadingIcon";
import ZoomImage from "../ZoomImage/ZoomImage";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import { authAndDownloadImage, deleteImageFromStorage } from "../../shared_components/utils/ImgUtils.js";
import { AuthContext } from '../../context/AuthContext';

// Importing icons
import notes_icon           from '../../assets/icons/lightblue/document.png';
import close_icon           from '../../assets/icons/close.svg';
import back_icon_white      from '../../assets/icons/white/back_small.png';
import forward_icon_white   from '../../assets/icons/white/forward_small.png';
import download_icon        from '../../assets/icons/white/download.png';
import trash_icon           from '../../assets/icons/white/trash.svg';

function ClaimImageView({ delivery, claimImageId, setClaimImageId, handleClaimFieldChange }) {
    const { i18n, t } = useTranslation();
    const [ showDeleteImageModal, setShowDeleteImageModal ] = React.useState(false);
    const [ portalReady, setPortalReady ] = React.useState(false);
    
    // Create a ref for the portal container
    const portalContainerRef = React.useRef(null);
    
    // Create portal container on mount
    React.useEffect(() => {
        // Check if portal container already exists
        let container = document.getElementById('claim-image-view-portal');
        if (!container) {
            container = document.createElement('div');
            container.id = 'claim-image-view-portal';
            document.body.appendChild(container);
        }
        
        portalContainerRef.current = container;
        setPortalReady(true);
        
        // Clean up on unmount
        return () => {
            if (container && container.parentNode && !claimImageId) {
                document.body.removeChild(container);
            }
        };
    }, []);

    function scrollClaimImage(scrollAmount) {
        /* Change the displayed image by scrolling through scrollAmount images. */
        if (!claimImageId || !scrollAmount) return;

        const imageIds = Object.keys(delivery?.claim_data?.claim_images).sort();
        const currentIndex = imageIds.indexOf(claimImageId);
        if (currentIndex === -1) return;

        const nextIndex = currentIndex + scrollAmount;
        if (nextIndex >= imageIds.length || nextIndex < 0) return;
        const nextImageId = imageIds[nextIndex];
        setClaimImageId(nextImageId);
    }

    async function handleDeleteImage() {
        /* Delete the note from the claim */
        const imageId = claimImageId;
        setShowDeleteImageModal(false);
        setClaimImageId(null);

        // Delete the image from the storage
        deleteImageFromStorage(
            delivery.claim_data.claim_images[imageId].image_URL,
            ["_400x400", "_1920x1920"]
        );

        // Delete the note from the database
        handleClaimFieldChange({target: {name: [imageId], value: deleteField()}});
    }

    // Add event listener for keyboard navigation
    React.useEffect(() => {
        /* Add event listener for arrow keys to navigate between images */
        function handleKeyDown(event) {
            // Check for left arrow key
            if (event.keyCode === 37) {
                scrollClaimImage(-1);
            }
    
            // Check for right arrow key
            if (event.keyCode === 39) {
                scrollClaimImage(1);
            }
        }
        document.addEventListener('keydown', handleKeyDown);
        return () => { document.removeEventListener('keydown', handleKeyDown); }
    }, [claimImageId]);

    // If portal container isn't ready or no claimImageId, return null
    if (!portalReady || !claimImageId) return null;

    // The JSX to render
    const claimImageViewContent = (
        <div className="fullscreen-bale-modal">
            <div className="fullscreen-modal--buttons">
                <div className="fullscreen-modal--button fullscreen-modal--next" onClick={() => scrollClaimImage(-1)}>
                    <img className="fullscreen-modal--button-icon" src={back_icon_white}/>
                </div>
            </div>
            <div className="fullscreen-modal--center">
                <div className="fullscreen-modal--img-wrapper">
                    <ZoomImage image_url={delivery?.claim_data?.claim_images?.[claimImageId]?.image_URL} />
                </div>
                <div className="center-footer">
                    <PropertyField 
                        icon={notes_icon} 
                        title={t("property_notes").toUpperCase()} 
                        value={delivery.claim_data.claim_images[claimImageId]?.notes ?? ""} 
                        name="notes"
                        type="textarea"
                        handleChange={(e) => handleClaimFieldChange({target: {name: [claimImageId], value: {notes: e.target.value}}})}
                        editable={true}
                        grid_span={1}
                    />
                </div>
            </div>
            <div className="fullscreen-modal--buttons">
                <div className="fullscreen-modal--button" onClick={() => setClaimImageId(null)}>
                    <img className="fullscreen-modal--button-icon" src={close_icon}/>
                </div>
                <div className="fullscreen-modal--button" onClick={() => {
                    authAndDownloadImage(storage, delivery.claim_data.claim_images[claimImageId]?.image_URL, `claim_${delivery?.supplier_id ?? 'unknown'}_${format(delivery.date, 'yyyy-MM-dd_HH-mm')}_${Object.keys(delivery.claim_data.claim_images).indexOf(claimImageId)}.jpg`)}}>
                    <img className="fullscreen-modal--button-icon" src={download_icon}/>
                </div>
                <div className="fullscreen-modal--button" onClick={() => {setShowDeleteImageModal(true)}}>
                    <img className="fullscreen-modal--button-icon" src={trash_icon} style={{height: "80%"}}/>
                </div>
                <div className="fullscreen-modal--button fullscreen-modal--next" onClick={() => scrollClaimImage(1)}>
                    <img className="fullscreen-modal--button-icon" src={forward_icon_white}/>
                </div>
            </div>

            {/* Show modal to delete a note */}
            <ConfirmationModal 
                show={showDeleteImageModal}
                setShow={setShowDeleteImageModal}
                title={t("claim_delete_note_title")}
                onConfirm={handleDeleteImage}
            />
        </div>
    );

    // Render using portal
    return ReactDOM.createPortal(claimImageViewContent, portalContainerRef.current);
}

function ClaimReportButton({ delivery }) {
    const { i18n, t } = useTranslation();
    const [ showDownloadReportModal, setShowDownloadReportModal ] = React.useState(false);
    const [ reportDownloadLinks, setReportDownloadLinks ] = React.useState(null);
    const [ reportDownloadError, setReportDownloadError ] = React.useState(null);
    const { logEventCustom } = React.useContext(AuthContext);

    React.useEffect(() => {
        // Reset download links
        if (!showDownloadReportModal) {
            setReportDownloadLinks(null);
        }
    }, [delivery]);

    async function handleReportDownload() {
        /*
        Download claim report. 
        Open the modal and call function to generate the reports.
        Once the function responds, show the download links.
        */
        setShowDownloadReportModal(true);

        // If download links are already loaded, do not call the function again (resets when claim is updated)
        if (reportDownloadLinks) {
            return;
        }

        // Call function to generate the report
        setReportDownloadLinks(null);
        setReportDownloadError(null);
        const getClaimReport = httpsCallable(functions, "onGetClaimReport", {
            timeout: 180000 // 3 minutes
        });
        try {
            const response = await getClaimReport({deliveryId: delivery.id});
            setReportDownloadLinks({
                pdf: response.data?.pdf_url,
                docx: response.data?.docx_url
            })
        } catch (error) {
            setReportDownloadError(error.message);
            console.error("Error: ", error, error.message);
        }
    }

    return (
        <>
            <button className="claim-download-report-button" onClick={handleReportDownload}>
                {t("claim_download_report")}
            </button>
            {/* Show modal to download the claim report as docx/pdf */}
            <Modal 
                className="default-modal"
                show={showDownloadReportModal}
                backdrop = "static"
                onHide={() => setShowDownloadReportModal(false)}
            >
                <Modal.Header>
                    <Modal.Title>{t("claim_download_report_title")}</Modal.Title>
                    <div className="btn-close" onClick={() => {setShowDownloadReportModal(false)}}><img src={close_icon}/></div>
                </Modal.Header>
                <Modal.Body>
                    {!reportDownloadLinks && !reportDownloadError && <div className="claim-report-loading">
                        <LoadingIcon/>
                        <p>{t("claim_download_report_generating")}</p>
                    </div>}
                    {reportDownloadError && 
                    <p className="claim-report-error">{t("claim_download_report_error")}</p>}
                </Modal.Body>
                {reportDownloadLinks && <Modal.Footer>
                    {reportDownloadLinks.docx && <Button variant="secondary" onClick={() => {
                        authAndDownloadImage(storage, reportDownloadLinks?.docx, `claim_${delivery?.supplier_id ?? 'unknown'}_${format(delivery.date, 'yyyy-MM-dd_HH-mm')}.docx`);
                        logEventCustom("claim_report_download", {content_id: delivery.id ,type: "docx"});
                    }}>
                        Download Docx
                    </Button>}
                    {reportDownloadLinks.pdf && <Button variant="primary" onClick={() => {
                        authAndDownloadImage(storage, reportDownloadLinks?.pdf, `claim_${delivery?.supplier_id ?? 'unknown'}_${format(delivery.date, 'yyyy-MM-dd_HH-mm')}.pdf`);
                        logEventCustom("claim_report_download", {content_id: delivery.id ,type: "pdf"});
                        }}>
                        Download PDF
                    </Button>}
                </Modal.Footer> }
            </Modal>
        </>
    )
}

export { ClaimImageView, ClaimReportButton };